// src/components/VirtualizedMessageList.tsx
import React, { useEffect, useRef, useCallback, useMemo, useState } from 'react';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { Box, CircularProgress, Typography, Chip, Paper, Skeleton } from '@mui/material';
import { Explore, Image as ImageIcon } from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { Message } from '../types';
import { MessageBubble } from './MessageBubble';
import { PendingIndicatorType } from '../hooks/useChat';
import { Logo } from './Logo';
import { useT } from '../i18n/useT';

interface VirtualizedMessageListProps {
  messages: Message[];
  isLoading: boolean;
  isStreaming: boolean;
  streamingContent: string;
  streamingStatus?: string;
  onInteractiveClick?: (message: string, element: any) => void;
  onFollowUpClick?: (prompt: string) => void;
  pendingIndicator?: PendingIndicatorType;
  isSending?: boolean;
  /** User's current coordinates, passed to the entity detail modal
   *  so Places lookups for nearby entities can bias to the right one
   *  when there are many places with similar names. */
  userLocation?: { lat: number; lng: number };
  /** Active conversation id. Forwarded to MessageBubble for the
   *  saved_message thumbs-up path (Phase 3.7.4). */
  conversationId?: string;
}

// Pending indicator component for visual feedback
const PendingIndicator: React.FC<{ indicator: PendingIndicatorType }> = ({ indicator }) => {
  const t = useT();
  if (!indicator) return null;

  if (indicator.type === 'location') {
    return (
      <Box sx={{ px: 2, py: 1 }}>
        <Paper 
          elevation={1}
          sx={{ 
            p: 2, 
            maxWidth: '85%',
            bgcolor: 'success.light',
            color: 'success.contrastText',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Explore sx={{ fontSize: 20 }} />
          <Box>
            <Typography variant="body2" fontWeight={500}>
              
              {t("🧭 Exploring your area...")}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              
              {t("Getting information about nearby points of interest")}
            </Typography>
          </Box>
          <CircularProgress size={16} sx={{ ml: 'auto', color: 'inherit' }} />
        </Paper>
      </Box>
    );
  }

  if (indicator.type === 'image') {
    return (
      <Box sx={{ px: 2, py: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Paper 
            elevation={1}
            sx={{ 
              p: 1.5, 
              maxWidth: '70%',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              borderRadius: 2,
            }}
          >
            {/* Show image thumbnail */}
            <Box
              component="img"
              src={indicator.imagePath}
              alt={t("Uploaded image")}
              sx={{
                width: '100%',
                maxWidth: 250,
                height: 'auto',
                maxHeight: 200,
                objectFit: 'cover',
                borderRadius: 1,
                display: 'block',
              }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <Typography variant="caption">
                
                {t("Analyzing image...")}
              </Typography>
              <CircularProgress size={12} sx={{ color: 'inherit' }} />
            </Box>
          </Paper>
        </Box>
      </Box>
    );
  }

  if (indicator.type === 'thinking') {
    return (
      <Box sx={{ px: 2, py: 1 }}>
        <Paper 
          elevation={1}
          sx={{ 
            p: 2, 
            maxWidth: '60%',
            bgcolor: 'grey.100',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
              '& > div': {
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: 'primary.main',
                animation: 'bounce 1.4s ease-in-out infinite',
                '&:nth-of-type(1)': { animationDelay: '0s' },
                '&:nth-of-type(2)': { animationDelay: '0.2s' },
                '&:nth-of-type(3)': { animationDelay: '0.4s' },
              },
              '@keyframes bounce': {
                '0%, 80%, 100%': { transform: 'scale(0.6)', opacity: 0.5 },
                '40%': { transform: 'scale(1)', opacity: 1 },
              },
            }}
          >
            <div />
            <div />
            <div />
          </Box>
          <Typography variant="body2" color="text.secondary">
            
            {t("Thinking...")}
          </Typography>
        </Paper>
      </Box>
    );
  }

  return null;
};

// Assistant typing indicator
const AssistantTypingIndicator: React.FC = () => { const t = useT(); return (
  <Box sx={{ px: 2, py: 1 }}>
    <Paper 
      elevation={1}
      sx={{ 
        p: 2, 
        maxWidth: '60%',
        bgcolor: 'grey.50',
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 0.5,
          '& > div': {
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: 'primary.main',
            animation: 'bounce 1.4s ease-in-out infinite',
            '&:nth-of-type(1)': { animationDelay: '0s' },
            '&:nth-of-type(2)': { animationDelay: '0.2s' },
            '&:nth-of-type(3)': { animationDelay: '0.4s' },
          },
          '@keyframes bounce': {
            '0%, 80%, 100%': { transform: 'scale(0.6)', opacity: 0.5 },
            '40%': { transform: 'scale(1)', opacity: 1 },
          },
        }}
      >
        <div />
        <div />
        <div />
      </Box>
      <Typography variant="body2" color="text.secondary">
        {t("AI is typing...")}
      </Typography>
    </Paper>
  </Box>
); };

export const VirtualizedMessageList: React.FC<VirtualizedMessageListProps> = ({
  messages,
  isLoading,
  isStreaming,
  streamingContent,
  streamingStatus,
  onInteractiveClick,
  onFollowUpClick,
  pendingIndicator,
  isSending,
  userLocation,
  conversationId,
}) => {
  const t = useT();
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  // Underlying scroll container. Captured via Virtuoso's
  // `scrollerRef` prop. We scroll this DOM node directly to reach
  // `scrollHeight` — `scrollToIndex({ index: 'LAST' })` only brings
  // the LAST item's TOP to the viewport top (default align), which
  // for a short user message leaves the message pinned high near
  // the floating header instead of just above the composer. See the
  // scroll helpers below for details.
  const scrollerRef = useRef<HTMLElement | null>(null);
  const isAtBottomRef = useRef(true);
  const shouldAutoScrollRef = useRef(true);
  const lastMessageCountRef = useRef<number>(0);
  const streamingMessageIdRef = useRef<string>(`streaming-${Date.now()}`);
  // Tracks a pending follow-up scroll so we don't stack timers
  // when several updates land in quick succession (e.g. multi-line
  // user message + image + reroute through pending indicator).
  const followupScrollTimerRef = useRef<number | null>(null);

  // ── Per-conversation initial-scroll latch ──────────────────────
  // When the user opens a conversation (or navigates between them),
  // we want the very first paint with messages to land at the
  // bottom — newest message visible — instead of at index 0 which
  // is what Virtuoso defaults to for a freshly-mounted list.
  //
  // Without this, opening a long thread shows you the OLDEST
  // message and any new send appears to "go to the top" because
  // Virtuoso never gets a chance to settle to the bottom before
  // atBottomStateChange fires `false` (we're at the top of a long
  // list) and pins shouldAutoScrollRef there.
  //
  // Latch resets whenever conversationId changes so each new
  // conversation gets its own initial jump.
  const hasInitiallyScrolledRef = useRef(false);
  const lastConversationIdRef = useRef<string | undefined>(conversationId);
  
  // Create display items including streaming message
  const displayItems = useMemo(() => {
    // Filter out temp messages that match saved messages
    const realMessages = messages.filter(m => !m.id.startsWith('temp_'));
    const tempMessages = messages.filter(m => m.id.startsWith('temp_'));
    
    // Only keep temp messages that don't have a matching saved message
    const uniqueTempMessages = tempMessages.filter(tempMsg => {
      const tempText = tempMsg.parts?.find(p => p.type === 'text')?.content;
      const tempImage = tempMsg.parts?.find(p => p.type === 'image')?.url;
      
      return !realMessages.some(realMsg => {
        const realText = realMsg.parts?.find(p => p.type === 'text')?.content;
        const realImage = realMsg.parts?.find(p => p.type === 'image')?.url;
        
        // Match by text content or image URL
        if (tempText && realText && tempText === realText) return true;
        if (tempImage && realImage && tempImage === realImage) return true;
        return false;
      });
    });
    
    const items = [...realMessages, ...uniqueTempMessages];
    
    // Sort by created_at
    items.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

    // Guard against duplicate renders (same role + content within a short window)
    const dedupedItems: Message[] = [];
    const normalizeText = (text?: string) =>
      (text || '').trim().replace(/\s+/g, ' ').toLowerCase();

    for (const item of items) {
      const itemText = normalizeText(item.parts?.find(p => p.type === 'text')?.content);
      const itemImage = item.parts?.find(p => p.type === 'image')?.url || '';
      const last = dedupedItems[dedupedItems.length - 1];

      if (last) {
        const lastText = normalizeText(last.parts?.find(p => p.type === 'text')?.content);
        const lastImage = last.parts?.find(p => p.type === 'image')?.url || '';
        const timeDiffMs = Math.abs(new Date(item.created_at).getTime() - new Date(last.created_at).getTime());

        const isSameContent = lastText === itemText && lastImage === itemImage;
        const isSameRole = last.role === item.role;
        const isNearInTime = timeDiffMs < 30000; // 30s window catches backend+frontend double-saves

        if (isSameRole && isSameContent && isNearInTime) {
          continue;
        }
      }

      dedupedItems.push(item);
    }

    let finalItems = dedupedItems;
    
    // Add streaming message if actively streaming with content
    if (isStreaming && streamingContent && streamingContent.trim()) {
      // Get the last assistant message
      const lastAssistantMessage = [...finalItems]
        .reverse()
        .find(m => m.role === 'assistant');
      
      const lastAssistantText = lastAssistantMessage?.parts
        ?.find(p => p.type === 'text')?.content || '';
      
      // IMPROVED: Better duplicate detection
      // Check if streaming content is substantially different from last saved message
      const contentLength = streamingContent.length;
      const savedLength = lastAssistantText.length;
      
      // Don't add if:
      // 1. Exact match
      // 2. Saved message starts with streaming (streaming is subset)
      // 3. Streaming starts with saved AND saved is > 90% of streaming length (almost complete)
      const isExactMatch = lastAssistantText === streamingContent;
      const savedContainsStreaming = lastAssistantText.startsWith(streamingContent);
      const streamingContainsSaved = streamingContent.startsWith(lastAssistantText) && 
        savedLength > contentLength * 0.9;
      
      const isDuplicate = isExactMatch || savedContainsStreaming || streamingContainsSaved;
      
      if (!isDuplicate) {
        // Use stable ID for streaming message to prevent flicker
        finalItems.push({
          id: streamingMessageIdRef.current,
          role: 'assistant' as const,
          parts: [{ type: 'text' as const, content: streamingContent }],
          created_at: new Date(),
          isStreaming: true,
        });
      }
    }
    
    return finalItems;
  }, [messages, isStreaming, streamingContent]);

  // Reset streaming message ID when streaming stops
  useEffect(() => {
    if (!isStreaming) {
      streamingMessageIdRef.current = `streaming-${Date.now()}`;
    }
  }, [isStreaming]);

  // Track scroll position. Re-enable auto-scroll whenever the user
  // lands back at the bottom — common pattern: they scroll up to read
  // older messages, then scroll back down. Before this fix, auto-scroll
  // stayed disabled forever and new messages would silently appear
  // off-screen.
  //
  // We deliberately ignore atBottom changes BEFORE the first
  // initial scroll completes. Otherwise Virtuoso fires `false` on
  // its very first mount-with-data tick (it lands at index 0 of a
  // long list) and we'd pin shouldAutoScroll = false before we've
  // had a chance to jump to the actual bottom.
  const handleAtBottomStateChange = useCallback((atBottom: boolean) => {
    isAtBottomRef.current = atBottom;
    if (hasInitiallyScrolledRef.current) {
      shouldAutoScrollRef.current = atBottom;
    }
  }, []);

  // Reset per-conversation state whenever conversationId changes.
  // Without this, navigating from a long thread to a short one (or
  // vice-versa) carries over `lastMessageCountRef` and the new
  // message effect either misfires or stays silent — producing
  // the symptom "I sent a message but the view stayed at the top".
  useEffect(() => {
    if (lastConversationIdRef.current !== conversationId) {
      lastConversationIdRef.current = conversationId;
      lastMessageCountRef.current = 0;
      shouldAutoScrollRef.current = true;
      isAtBottomRef.current = true;
      hasInitiallyScrolledRef.current = false;
    }
  }, [conversationId]);

  // Re-enable auto-scroll when new messages arrive
  useEffect(() => {
    if (isAtBottomRef.current) {
      shouldAutoScrollRef.current = true;
    }
  }, [messages.length]);

  // ── Scroll-to-bottom helper ──
  //
  // Why we don't use `virtuosoRef.current.scrollToIndex({ index:
  // 'LAST' })` here: Virtuoso's `scrollToIndex` defaults to
  // `align: 'start'`, which scrolls until the TOP of the last item
  // sits at the top of the viewport. For a one-line user message
  // that leaves the message pinned just under the floating frosted
  // header with a long stretch of empty Footer-padding below — the
  // composer (anchored bottom: var(--app-bottom-nav-h)) sits over
  // that empty space, so visually it looks like "the chat didn't
  // scroll to my message at all." `align: 'end'` is also wrong: it
  // would tuck the message UNDER the composer overlay.
  //
  // What works: scroll the underlying scroll container to
  // `scrollHeight`. The Footer already carries a
  // `calc(220px + var(--app-bottom-nav-h))` bottom-padding spacer
  // sized exactly to clear the composer cluster. So scrolling all
  // the way to the bottom of the scroll content lands the last
  // message right above the composer — which is the natural place
  // the user expects to see what they just sent.
  const scrollScrollerToBottom = useCallback(
    (behavior: ScrollBehavior = 'smooth') => {
      const el = scrollerRef.current;
      if (!el) return;
      el.scrollTo({ top: el.scrollHeight, behavior });
    },
    []
  );

  // Schedule a follow-up scroll. Newly-added messages — especially
  // ones with images — settle their final height across multiple
  // frames: react-virtuoso renders the item, ResizeObserver fires,
  // images load, layouts reflow. A single scroll on commit lands at
  // the OLD scrollHeight. We rAF once for the next layout tick, and
  // schedule a delayed second pass to catch image loads / late
  // resizes. Both calls are idempotent: if we're already at the
  // bottom, scrollTo is a no-op.
  const scheduleScrollToBottom = useCallback(
    (behavior: ScrollBehavior = 'smooth') => {
      requestAnimationFrame(() => scrollScrollerToBottom(behavior));
      if (followupScrollTimerRef.current !== null) {
        window.clearTimeout(followupScrollTimerRef.current);
      }
      followupScrollTimerRef.current = window.setTimeout(() => {
        followupScrollTimerRef.current = null;
        scrollScrollerToBottom(behavior);
      }, 350);
    },
    [scrollScrollerToBottom]
  );

  // Cleanup the follow-up timer on unmount so it doesn't fire
  // against a stale scroller.
  useEffect(() => {
    return () => {
      if (followupScrollTimerRef.current !== null) {
        window.clearTimeout(followupScrollTimerRef.current);
        followupScrollTimerRef.current = null;
      }
    };
  }, []);

  // Scroll to bottom — respects the "user is reading history" flag.
  // Use this for AI responses arriving and pending indicators.
  const scrollToBottom = useCallback(
    (behavior: ScrollBehavior = 'smooth') => {
      if (!shouldAutoScrollRef.current) return;
      scheduleScrollToBottom(behavior);
    },
    [scheduleScrollToBottom]
  );

  // Force-scroll, ignoring the flag. Use this when the user clearly
  // wants to see the bottom — i.e. they just sent a message.
  const scrollToBottomForced = useCallback(
    (behavior: ScrollBehavior = 'smooth') => {
      shouldAutoScrollRef.current = true;
      isAtBottomRef.current = true;
      scheduleScrollToBottom(behavior);
    },
    [scheduleScrollToBottom]
  );

  // Handle new messages. If the new message is from the user, force-scroll
  // (they just hit send — they want to see the bottom). If it's from the
  // assistant, only scroll when the user was already at the bottom.
  //
  // First-load case (initial render with messages for this
  // conversation): jump to bottom INSTANTLY (no smooth) so the
  // user lands on the latest exchange. This is a separate branch
  // because the incremental "new message" logic only fires on
  // count growth and we want the same behavior even when the
  // count went 0 → N atomically (which is what conversation-load
  // looks like).
  useEffect(() => {
    if (!hasInitiallyScrolledRef.current && messages.length > 0) {
      // If a deep-link hash scroll is pending (#message-X in the
      // URL), defer to that — the deep-link effect needs the list
      // NOT to be pinned to the bottom or it'll fight the
      // requested target. Read the hash directly to avoid a
      // forward reference to the deep-link state declared further
      // down the component body.
      const hasDeepLinkHash =
        typeof window !== 'undefined' &&
        /^#message-/.test(window.location.hash || '');
      if (hasDeepLinkHash) {
        // Mark initial scroll as handled so atBottomStateChange
        // can take over normally; the deep-link effect itself
        // already sets shouldAutoScrollRef = false.
        hasInitiallyScrolledRef.current = true;
        lastMessageCountRef.current = messages.length;
        return;
      }
      // Initial jump: instant (no smooth) — user just opened this
      // conversation, they expect to land on the latest exchange,
      // not watch a scroll animation play from the top. We rAF
      // through scheduleScrollToBottom so Virtuoso has measured
      // the freshly-mounted items before we read scrollHeight.
      scheduleScrollToBottom('auto');
      hasInitiallyScrolledRef.current = true;
      shouldAutoScrollRef.current = true;
      isAtBottomRef.current = true;
      lastMessageCountRef.current = messages.length;
      return;
    }

    if (messages.length > lastMessageCountRef.current) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage?.role === 'user') {
        scrollToBottomForced('smooth');
      } else {
        scrollToBottom('smooth');
      }
      lastMessageCountRef.current = messages.length;
    }
  }, [messages, scrollToBottom, scrollToBottomForced]);

  // Handle streaming content updates — one scroll per animation frame,
  // not per-100-chars. The previous approach produced visible stutter
  // on fast-streaming responses; rAF smooths it out without costing perf.
  const rafRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isStreaming || !streamingContent || !shouldAutoScrollRef.current) {
      return;
    }
    if (rafRef.current !== null) return; // already scheduled this frame
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      if (shouldAutoScrollRef.current) {
        scrollScrollerToBottom('auto');
      }
    });
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [streamingContent, isStreaming, scrollScrollerToBottom]);

  // Scroll when pending indicator appears
  useEffect(() => {
    if (pendingIndicator) {
      scrollToBottom('smooth');
    }
  }, [pendingIndicator, scrollToBottom]);

  // ── Deep-link scroll-to-message ──
  //
  // Saved feed cards link back to /chat/{convId}#message-{msgId}.
  // When that hash is present we want to:
  //   1. wait until the message is loaded (subscriptions are async),
  //   2. scroll to its index in the virtualized list,
  //   3. briefly highlight it so the user can spot it on landing.
  //
  // The hash can land in two ways:
  //   - the route remounts (different conversation): location.hash
  //     is set on first render, we attempt scroll on every messages
  //     update until the target is found, then clear the pending id.
  //   - the route is the same conversation but the hash changes
  //     (re-tap the same CTA): the hash effect fires on hash change
  //     and re-arms.
  //
  // We override auto-scroll-to-bottom while the deep link is pending
  // — without this, the "new messages arrived" effect would yank the
  // viewport down to the latest just as we're trying to focus an
  // older message.
  const location = useLocation();
  const [pendingScrollMessageId, setPendingScrollMessageId] = useState<
    string | null
  >(null);
  const [highlightedMessageId, setHighlightedMessageId] = useState<
    string | null
  >(null);
  const highlightTimerRef = useRef<number | null>(null);

  useEffect(() => {
    const match = location.hash.match(/^#message-(.+)$/);
    if (!match) return;
    const targetId = decodeURIComponent(match[1]);
    setPendingScrollMessageId(targetId);
    // While we're hunting for the target, suppress the bottom-pin
    // so a late-arriving message doesn't bounce us back down.
    shouldAutoScrollRef.current = false;
    isAtBottomRef.current = false;
  }, [location.hash, location.key]);

  useEffect(() => {
    if (!pendingScrollMessageId) return;
    if (!virtuosoRef.current) return;
    const idx = displayItems.findIndex(
      (m) => m?.id === pendingScrollMessageId
    );
    if (idx < 0) return; // not loaded yet — try again on next update
    virtuosoRef.current.scrollToIndex({
      index: idx,
      behavior: 'smooth',
      align: 'center',
    });
    setHighlightedMessageId(pendingScrollMessageId);
    setPendingScrollMessageId(null);
    if (highlightTimerRef.current !== null) {
      window.clearTimeout(highlightTimerRef.current);
    }
    // Keep the highlight visible for ~2.5s so the user clearly sees
    // which message they were deep-linked to. Then fade out by
    // clearing the id (the wrapper transitions the bgcolor).
    highlightTimerRef.current = window.setTimeout(() => {
      setHighlightedMessageId(null);
      highlightTimerRef.current = null;
    }, 2500);
  }, [pendingScrollMessageId, displayItems]);

  useEffect(() => {
    return () => {
      if (highlightTimerRef.current !== null) {
        window.clearTimeout(highlightTimerRef.current);
      }
    };
  }, []);

  const ItemContent = useCallback((index: number, item: any) => {
    const isHighlighted = !!item?.id && item.id === highlightedMessageId;
    return (
      <Box
        sx={{
          px: 2,
          // Subtle pulse when the user lands on this message via a
          // deep link. Auto-clears via highlightedMessageId state.
          backgroundColor: isHighlighted
            ? 'rgba(25, 118, 210, 0.10)'
            : 'transparent',
          borderRadius: 1,
          transition: 'background-color 600ms ease-out',
        }}
      >
        <MessageBubble
          message={item}
          onInteractiveClick={onInteractiveClick}
          onFollowUpClick={onFollowUpClick}
          recentMessages={messages}
          userLocation={userLocation}
          conversationId={conversationId}
        />
      </Box>
    );
  }, [onInteractiveClick, onFollowUpClick, messages, userLocation, highlightedMessageId]);

  const Footer = useMemo(() => {
    // Show pending indicator first (location exploring, image analyzing, thinking)
    if (pendingIndicator && !isStreaming && !streamingContent) {
      return (
        <Box sx={{ pb: 2 }}>
          <PendingIndicator indicator={pendingIndicator} />
        </Box>
      );
    }

    if (isLoading) {
      return (
        <Box display="flex" alignItems="center" justifyContent="center" py={4}>
          <CircularProgress size={24} sx={{ mr: 2 }} />
          <Typography variant="body2" color="text.secondary">
            
            {t("Loading messages...")}
          </Typography>
        </Box>
      );
    }

    // Show typing indicator when streaming but no content yet
    if (isStreaming && !streamingContent) {
      return (
        <Box sx={{ pb: 2 }}>
          <AssistantTypingIndicator />
        </Box>
      );
    }

    // Show status when streaming with content
    if (isStreaming && streamingContent) {
      return (
        <Box sx={{ pb: 2 }}>
          <Box 
            display="flex" 
            alignItems="center" 
            justifyContent="center" 
            gap={2}
            mt={2}
            px={2}
          >
            <CircularProgress size={16} />
            <Chip 
              label={streamingStatus || t('AI is responding...')}
              size="small"
              variant="outlined"
              sx={{ 
                backgroundColor: 'background.paper',
                borderColor: 'primary.main',
                color: 'primary.main',
                fontSize: '0.75rem'
              }}
            />
          </Box>
        </Box>
      );
    }

    // Show thinking indicator if sending but not yet streaming
    if (isSending && !isStreaming && !pendingIndicator) {
      return (
        <Box sx={{ pb: 2 }}>
          <AssistantTypingIndicator />
        </Box>
      );
    }

    return <Box sx={{ pb: 2 }} />;
  }, [isLoading, isStreaming, streamingStatus, streamingContent, pendingIndicator, isSending]);

  // Empty state
  if (displayItems.length === 0 && !isLoading && !isStreaming && !streamingContent && !pendingIndicator && !isSending) {
    return (
      <Box 
        display="flex" 
        flexDirection="column" 
        alignItems="center" 
        justifyContent="center" 
        height="100%" 
        p={4}
        textAlign="center"
      >
        <Logo size={38} alt={t("Instant Tours")} sx={{ mb: 2 }} />
        <Typography variant="h5" gutterBottom color="text.secondary">
          
          {t("Welcome")}
        </Typography>
        <Typography variant="body1" color="text.secondary" maxWidth={400}>
          
          {t("Take a photo of your surroundings and I'll be your AI tour guide, sharing fascinating stories and insights about what you're seeing.")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', width: '100%' }}>
      <Virtuoso
        ref={virtuosoRef}
        scrollerRef={(el) => {
          // Capture the underlying scroll DOM element so the scroll
          // helpers above can read scrollHeight and call
          // scrollTo({ top, behavior }) directly. Virtuoso re-invokes
          // this with `null` on unmount.
          scrollerRef.current = (el as HTMLElement | null) ?? null;
        }}
        data={displayItems}
        itemContent={ItemContent}
        components={{
          // Top spacer inside the scroll content — NOT padding on the
          // scroll container. This is what gives the floating frosted
          // header in AppShell something visible behind it: as the
          // user scrolls older messages into view, they pass *under*
          // the header (still inside the scroll viewport), and the
          // header's backdrop-filter blurs them. With `pt: 2` only,
          // the messages live in a flat zone beneath the header and
          // never enter the y=0 region the header occupies.
          Header: () => (
            <Box
              sx={{ paddingTop: 'calc(var(--app-header-h) + 16px)' }}
            />
          ),
          Footer: () => (
            <>
              {Footer}
              {/* Bottom spacer — the chat outer no longer pads itself
                  for the floating bottom nav OR the absolute-positioned
                  composer/strips cluster (see ChatInterface.tsx). The
                  message list is full column height, so we add the
                  clearance here, INSIDE the scroll content. ~220px is
                  a generous estimate of composer + strips height (worst
                  case: composer multi-line + suggestions visible). When
                  the user is fully scrolled to bottom, the latest
                  message ends just above the composer; mid-scroll, the
                  messages pass under the composer (hidden by it) and
                  continue under the bottom nav (visible blurred through
                  the frosted glass). */}
              <Box
                sx={{
                  paddingBottom:
                    'calc(220px + var(--app-bottom-nav-h))',
                }}
              />
            </>
          ),
        }}
        followOutput={false}
        alignToBottom
        atBottomStateChange={handleAtBottomStateChange}
        style={{ height: '100%' }}
        overscan={200}
      />
    </Box>
  );
};